/**
 * Color picker: the house palette as swatches, plus a free hex field for anything
 * else. Colors are stored in D1 (budget_categories.color, family_members.color)
 * and used across the tracker, chores, calendar and location — so this is config,
 * not decoration.
 *
 * The palette below is a set of *suggestions* drawn from the design tokens in
 * src/styles.css. The hex field means it never constrains what can be stored.
 */
import { useId } from 'react';
import { Field, TextInput } from './ui';

/** The house palette. Names matter: a swatch grid can't rely on color alone. */
export const PALETTE: { name: string; hex: string }[] = [
  { name: 'Sage', hex: '#2f6f4e' },
  { name: 'Clay', hex: '#b4553f' },
  { name: 'Honey', hex: '#c98a3c' },
  { name: 'Dusk', hex: '#5b6ea8' },
  { name: 'Moss', hex: '#6d8f5e' },
  { name: 'Plum', hex: '#7d5a86' },
  { name: 'Teal', hex: '#2f6f6f' },
  { name: 'Slate', hex: '#6b7280' },
];

const HEX = /^#[0-9a-f]{6}$/i;

export function isHex(value: string): boolean {
  return HEX.test(value.trim());
}

export function ColorField({
  value,
  onChange,
  label,
  hint,
  error,
}: {
  value: string;
  onChange: (hex: string) => void;
  label: string;
  hint?: string;
  error?: string | null;
}) {
  const groupId = useId();
  const normalized = value.trim().toLowerCase();
  const swatchName = PALETTE.find((p) => p.hex === normalized)?.name;

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-labelledby={groupId} className="flex flex-col gap-1.5">
        <span id={groupId} className="text-sm font-medium text-ink">
          {label}
        </span>
        {hint && <p className="text-xs text-ink-soft">{hint}</p>}

        <div className="flex flex-wrap gap-2 pt-1">
          {PALETTE.map((swatch) => {
            const selected = swatch.hex === normalized;
            return (
              <button
                key={swatch.hex}
                type="button"
                aria-pressed={selected}
                aria-label={swatch.name}
                title={swatch.name}
                onClick={() => onChange(swatch.hex)}
                className={`grid h-11 w-11 place-items-center rounded-full border-2 transition-transform focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none ${
                  selected ? 'border-ink scale-105' : 'border-border hover:scale-105'
                }`}
                style={{ backgroundColor: swatch.hex }}
              >
                {selected && (
                  <span aria-hidden="true" className="text-sm font-bold text-white drop-shadow">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Field label="Or a custom hex" error={error}>
            {(props) => (
              <TextInput
                {...props}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#2f6f4e"
                spellCheck={false}
                autoCapitalize="none"
                maxLength={7}
              />
            )}
          </Field>
        </div>
        <div className="flex items-center gap-2 pb-1">
          <span
            aria-hidden="true"
            className="block h-11 w-11 shrink-0 rounded-full border border-border"
            style={{ backgroundColor: isHex(value) ? normalized : 'transparent' }}
          />
          <span className="sr-only">
            {isHex(value) ? `Selected color ${swatchName ?? normalized}` : 'No valid color selected'}
          </span>
        </div>
      </div>
    </div>
  );
}
